import React, { useEffect, useRef, useState } from "react";
import { Button, Dropdown, Grid, GridColumn, Icon, Modal } from "semantic-ui-react";
import { toast } from "react-semantic-toasts";

const GoldRush = ({
    templateId,
    templateMonthOptions = [],
    selectedTemplate = "",
    onTemplateChange,
    getGoldRushDataApi,
    goldRushMappingApi,
    goldRushStartProcessApi
}) => {
    const [openMappingModal, setOpenMappingModal] = useState(false);
    const [mappingFile, setMappingFile] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [starting, setStarting] = useState(false);

    // Mapping input state
    const [uploadedFileName, setUploadedFileName] = useState("");
    const [uploadedFileUrl, setUploadedFileUrl] = useState("");

    // Process card state (derived from GET response)
    const [goldRushInput, setGoldRushInput] = useState(null);         // raw_file_process.gold_rush
    const [psrConcatAll, setPsrConcatAll] = useState(null);           // raw_file_process.psr_concat_all
    const [goldRushInputFile, setGoldRushInputFile] = useState(null); // gold_rush_input.input_data
    const [outputDownloadUrl, setOutputDownloadUrl] = useState("");   // raw_file_process.gold_rush.download_url
    const [outputResultUrl, setOutputResultUrl] = useState("");       // gold_rush_input.output_data.output_download_url
    const [processStatus, setProcessStatus] = useState("");           // raw_file_process.gold_rush.record.fileStatus
    const [inputProcessStatus, setInputProcessStatus] = useState(""); // gold_rush_input.process_status
    const [lastRunType, setLastRunType] = useState("");               // gold_rush_input.last_run_type
    const lastInteractionPollAt = useRef(0);

    // ── helpers ───────────────────────────────────────────────────────────────

    const extractMappingMeta = (res) => {
        const payload = res?.data || res || {};
        const inputData = payload?.gold_rush_input?.input_data || {};
        const fileKey = inputData?.file_key || "";
        const name = fileKey ? fileKey.split("/").pop() : "";
        const url = inputData?.download_url || "";
        return { name, url };
    };

    const extractProcessData = (res) => {
        const d = res?.data || res || {};

        // gold_rush_input.input_data  →  drives "Mapping" dot
        const grInput = d?.gold_rush_input?.input_data ?? null;

        // raw_file_process.gold_rush  →  drives "Gold Rush" dot
        const rawProcess = d?.raw_file_process ?? {};
        const gr  = rawProcess?.gold_rush ?? null;

        // raw_file_process.psr_concat_all  →  drives "PSR Concat" dot
        const psr = rawProcess?.psr_concat_all ?? null;

        // Download button URL  →  raw_file_process.gold_rush.download_url
        const outputUrl = gr?.download_url ?? "";

        // Status badge  →  prefer gold_rush_input.process_status
        const inputStatus = d?.gold_rush_input?.process_status ?? "";
        const status      = gr?.record?.fileStatus ?? "";

        // Last run type
        const runType = d?.gold_rush_input?.last_run_type ?? "";

        // Start vs Rerun  →  gold_rush_input.output_data.output_download_url
        const outputResult = d?.gold_rush_input?.output_data?.output_download_url ?? "";

        return { grInput, gr, psr, outputUrl, status, outputResult, inputStatus, runType };
    };

    // ── derived flags (computed from state on every render) ───────────────────

    // "Mapping" dot  →  gold_rush_input.input_data has file_key
    const hasGoldRushInput = !!(goldRushInputFile?.file_key);

    // "Gold Rush" dot  →  raw_file_process.gold_rush has file_key
    const hasGoldRushFile  = !!(goldRushInput?.file_key);

    // "PSR Concat" dot  →  raw_file_process.psr_concat_all has file_key
    const hasPsrFile       = !!(psrConcatAll?.file_key);

    const allInputsReady   = hasGoldRushInput && hasGoldRushFile && hasPsrFile;

    // Download button uses output_data.output_download_url (the processed output file)
    const hasOutput        = !!outputResultUrl;
    const hasOutputResult  = !!outputResultUrl;   // true → Rerun, false → Start
    const effectiveStatus  = (inputProcessStatus || "").toLowerCase();
    const isProcessing     = effectiveStatus === "processing";
    const displayStatus    = effectiveStatus || "pending";

    // ── data loading ──────────────────────────────────────────────────────────

    const loadGoldRushMappingData = () => {
        if (!templateId) return;
        getGoldRushDataApi(templateId, {
            onSuccess: (res) => {
                // Mapping modal meta
                const meta = extractMappingMeta(res);
                setUploadedFileName(meta.name);
                setUploadedFileUrl(meta.url);

                // Card state
                const {
                    grInput,
                    gr,
                    psr,
                    outputUrl,
                    status,
                    outputResult,
                    inputStatus,
                    runType
                } = extractProcessData(res);

                setGoldRushInputFile(grInput);   // gold_rush_input.input_data   → Mapping dot
                setGoldRushInput(gr);            // raw_file_process.gold_rush   → Gold Rush dot
                setPsrConcatAll(psr);            // raw_file_process.psr_concat_all → PSR dot
                setOutputDownloadUrl(outputUrl); // for Download button
                setOutputResultUrl(outputResult);// for Start vs Rerun
                setProcessStatus(status);        // fallback status
                setInputProcessStatus(inputStatus);
                setLastRunType(runType);
            },
            onFailure: () => {
                setUploadedFileName("");
                setUploadedFileUrl("");
                setGoldRushInputFile(null);
                setGoldRushInput(null);
                setPsrConcatAll(null);
                setOutputDownloadUrl("");
                setOutputResultUrl("");
                setProcessStatus("");
                setInputProcessStatus("");
                setLastRunType("");
            }
        });
    };

    useEffect(() => {
        loadGoldRushMappingData();
    }, [templateId]);

    const pollOnInteraction = () => {
        if (!templateId) return;
        const now = Date.now();
        // Throttle interaction polling to avoid excessive API calls.
        if (now - lastInteractionPollAt.current < 1200) return;
        lastInteractionPollAt.current = now;
        loadGoldRushMappingData();
    };

    const showToast = (type, title) => {
        toast({
            type,
            title,
            description: "",
            time: 2500,
        });
    };

    // ── actions ───────────────────────────────────────────────────────────────

    const onUploadMapping = () => {
        if (!mappingFile || !templateId) return;
        const formData = new FormData();
        formData.append("file", mappingFile);
        formData.append("template_id", templateId);
        const method = uploadedFileName ? "PUT" : "POST";
        setUploading(true);
        goldRushMappingApi(formData, method, {
            onSuccess: () => {
                setUploading(false);
                setOpenMappingModal(false);
                setMappingFile(null);
                loadGoldRushMappingData();
                showToast("success", uploadedFileName ? "Mapping file updated successfully" : "Mapping file uploaded successfully");
            },
            onFailure: (err) => {
                setUploading(false);
                console.error(err);
                showToast("error", "Failed to upload mapping file");
            }
        });
    };

    const onStartOrRerun = () => {
        if (!templateId || !allInputsReady) return;
        const runType = hasOutputResult ? "rerun" : "start";
        setStarting(true);
        goldRushStartProcessApi({ template_id: templateId, runtype: runType }, {
            onSuccess: () => {
                setStarting(false);
                loadGoldRushMappingData();
                showToast("success", `Process ${runType === "rerun" ? "re-run" : "started"} successfully`);
            },
            onFailure: (err) => {
                setStarting(false);
                console.error(err);
                showToast("error", "Failed to start process");
            }
        });
    };

    // ── status badge ──────────────────────────────────────────────────────────

    const StatusBadge = ({ status }) => {
        const map = {
            completed:  { color: "#065F46", bg: "#D1FAE5", border: "#6EE7B7" },
            processing: { color: "#92400E", bg: "#FEF3C7", border: "#FCD34D" },
            failed:     { color: "#991B1B", bg: "#FEE2E2", border: "#FCA5A5" },
            pending:    { color: "#6B7280", bg: "#F3F4F6", border: "#D1D5DB" },
        };
        const key = (status || "pending").toLowerCase();
        const s = map[key] || map.pending;
        return (
            <span style={{
                fontSize: 11, fontWeight: 600, padding: "2px 8px",
                borderRadius: 99, border: `1px solid ${s.border}`,
                background: s.bg, color: s.color, textTransform: "capitalize"
            }}>
                {status || "Pending"}
            </span>
        );
    };

    // ── render ────────────────────────────────────────────────────────────────

    return (
        <div onClickCapture={pollOnInteraction}>
            {/* Top action bar */}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 20 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <Dropdown
                        placeholder='Select Month'
                        search
                        selection
                        options={templateMonthOptions}
                        onChange={onTemplateChange}
                        value={selectedTemplate}
                        style={{ minWidth: 240 }}
                    />
                    <Button basic color='blue' onClick={() => setOpenMappingModal(true)}>
                        <Icon name='upload' />Upload Mapping
                    </Button>
                </div>
            </div>

            {/* Cards row */}
            <Grid columns={4} style={{ marginTop: 10 }}>
                {/* Process runner card */}
                <GridColumn>
                    <div style={{
                        border: "1px solid #E5E7EB",
                        borderRadius: 10,
                        padding: "16px 18px",
                        background: "#fff",
                        height: "100%",
                        display: "flex",
                        flexDirection: "column",
                        gap: 12
                    }}>
                        {/* Card header */}
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                            <div>
                                <div style={{ fontWeight: 700, fontSize: 15, color: "#111827" }}>Gold Rush Process</div>
                                <div style={{ fontSize: 12, color: "#6B7280", marginTop: 3 }}>Gold Rush Consolidated Run</div>
                            </div>
                            <StatusBadge status={displayStatus} />
                        </div>

                        {/* Compact input availability */}
                        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                            {[
                                { label: "Mapping",    ready: hasGoldRushInput },
                                { label: "Gold Rush",  ready: hasGoldRushFile  },
                                { label: "PSR Concat", ready: hasPsrFile       },
                            ].map(({ label, ready }) => (
                                <div key={label} style={{ display: "flex", alignItems: "center", gap: 4 }}>
                                    <span style={{
                                        width: 6, height: 6, borderRadius: "50%", flexShrink: 0,
                                        background: ready ? "#10B981" : "#D1D5DB"
                                    }} />
                                    <span style={{ fontSize: 11, color: ready ? "#374151" : "#9CA3AF" }}>{label}</span>
                                </div>
                            ))}
                        </div>

                        {/* Action buttons */}
                        <div style={{ display: "flex", gap: 8, marginTop: "auto" }}>
                            <Button
                                color={hasOutputResult ? "orange" : "red"}
                                basic
                                disabled={!allInputsReady || isProcessing || starting}
                                loading={starting}
                                onClick={onStartOrRerun}
                                style={{ flex: 1 }}
                            >
                                <Icon name={hasOutputResult ? "redo" : "play"} />
                                {hasOutputResult ? "Rerun" : "Start"}
                            </Button>
                            <Button
                                basic
                                color='green'
                                disabled={!hasOutput}
                                onClick={() => hasOutput && window.open(outputResultUrl)}
                                style={{ flex: 1 }}
                            >
                                <Icon name='download' />Download
                            </Button>
                        </div>
                    </div>
                </GridColumn>
            </Grid>

            {/* Upload mapping modal */}
            <Modal
                open={openMappingModal}
                onClose={() => { setOpenMappingModal(false); setMappingFile(null); }}
                size='small'
            >
                <Modal.Header style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <div style={{
                        width: 32, height: 32, borderRadius: 8, background: "#EFF6FF",
                        display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0
                    }}>
                        <Icon name='table' color='blue' style={{ margin: 0 }} />
                    </div>
                    <div>
                        <div style={{ fontSize: 15, fontWeight: 600, lineHeight: 1.3 }}>Gold Rush Mapping</div>
                        <div style={{ fontSize: 12, fontWeight: 400, color: "#6B7280", marginTop: 2 }}>
                            Upload or replace the column mapping file
                        </div>
                    </div>
                </Modal.Header>

                <Modal.Content style={{ paddingTop: 16, paddingBottom: 16 }}>
                    {uploadedFileName && (
                        <>
                            <div style={{
                                fontSize: 12, fontWeight: 600, color: "#6B7280", marginBottom: 6,
                                textTransform: "uppercase", letterSpacing: "0.05em"
                            }}>
                                Currently uploaded
                            </div>
                            <div style={{
                                display: "flex", alignItems: "center", gap: 10,
                                background: "#F9FAFB", border: "1px solid #E5E7EB",
                                borderRadius: 8, padding: "10px 14px", marginBottom: 12
                            }}>
                                <div style={{
                                    width: 36, height: 36, borderRadius: 8, background: "#D1FAE5",
                                    display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0
                                }}>
                                    <Icon name='file excel outline' color='green' style={{ margin: 0 }} />
                                </div>
                                <div style={{ flex: 1, minWidth: 0 }}>
                                    <div style={{
                                        fontSize: 13, fontWeight: 600, color: "#111827",
                                        overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap"
                                    }}>
                                        {uploadedFileName}
                                    </div>
                                    <div style={{ fontSize: 11, color: "#9CA3AF", marginTop: 2 }}>Mapping file · .xlsx</div>
                                </div>
                                <Button
                                    basic compact size='mini' color='blue'
                                    onClick={() => window.open(uploadedFileUrl)}
                                    disabled={!uploadedFileUrl}
                                    style={{ flexShrink: 0 }}
                                >
                                    <Icon name='download' />Download
                                </Button>
                            </div>
                            <div style={{
                                display: "flex", alignItems: "flex-start", gap: 8,
                                background: "#FFFBEB", border: "1px solid #FDE68A",
                                borderRadius: 8, padding: "8px 12px", marginBottom: 14
                            }}>
                                <Icon name='warning circle' color='yellow' style={{ margin: "1px 0 0", flexShrink: 0 }} />
                                <span style={{ fontSize: 12, color: "#92400E" }}>
                                    Uploading a new file will replace the existing mapping.
                                </span>
                            </div>
                        </>
                    )}

                    <div style={{
                        fontSize: 12, fontWeight: 600, color: "#6B7280", marginBottom: 6,
                        textTransform: "uppercase", letterSpacing: "0.05em"
                    }}>
                        {uploadedFileName ? "New file" : "Select file"}
                    </div>

                    {!mappingFile ? (
                        <label
                            style={{
                                display: "block", border: "1.5px dashed #D1D5DB", borderRadius: 8,
                                padding: "24px 16px", textAlign: "center", cursor: "pointer", background: "#FAFAFA"
                            }}
                            onDragOver={e => e.preventDefault()}
                            onDrop={e => {
                                e.preventDefault();
                                const f = e.dataTransfer.files[0];
                                if (f && f.name.endsWith(".xlsx")) setMappingFile(f);
                            }}
                        >
                            <Icon name='cloud upload' size='large' color='grey' />
                            <div>
                                <span style={{ color: "#2563EB", fontWeight: 500 }}>Upload</span>
                            </div>
                            <div style={{ fontSize: 11, color: "#9CA3AF", marginTop: 4 }}>Accepts .xlsx files only</div>
                            <input
                                type='file'
                                accept='.xlsx'
                                style={{ display: "none" }}
                                onChange={e => setMappingFile(e.target.files?.[0] || null)}
                            />
                        </label>
                    ) : (
                        <div style={{
                            display: "flex", alignItems: "center", gap: 8,
                            background: "#F0FDF4", border: "1px solid #BBF7D0",
                            borderRadius: 8, padding: "10px 14px"
                        }}>
                            <Icon name='file excel outline' color='green' style={{ margin: 0 }} />
                            <span style={{
                                fontSize: 13, color: "#111827", flex: 1, minWidth: 0,
                                overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap"
                            }}>
                                {mappingFile.name}
                            </span>
                            <Icon
                                name='times circle outline'
                                color='grey'
                                style={{ cursor: "pointer", margin: 0 }}
                                onClick={() => setMappingFile(null)}
                            />
                        </div>
                    )}
                </Modal.Content>

                <Modal.Actions>
                    <Button onClick={() => { setOpenMappingModal(false); setMappingFile(null); }}>
                        Cancel
                    </Button>
                    <Button
                        onClick={onUploadMapping}
                        color='blue'
                        disabled={!mappingFile || uploading || !templateId || isProcessing}
                        loading={uploading}
                        icon
                        labelPosition='left'
                    >
                        <Icon name='upload' />
                        {uploadedFileName ? "Update mapping" : "Upload mapping"}
                    </Button>
                </Modal.Actions>
            </Modal>
        </div>
    );
};

export default GoldRush;